import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Mail, Plus, Edit2, Trash2, Eye, Search, Filter,
  FileText, CheckCircle, AlertCircle, Copy, History, Zap, Globe, RefreshCw, XCircle
} from 'lucide-react';
import EmailTemplateEditor from './EmailTemplateEditor.jsx';
import EmailPreviewModal from './EmailPreviewModal.jsx';
import GeneratedEmailsHistory from './GeneratedEmailsHistory.jsx';
import WorkflowTriggersTab from './WorkflowTriggersTab.jsx';
import LanguageVariantsPanel from './LanguageVariantsPanel.jsx';

import { API_BASE_URL, getAuthHeaders as getCoreAuthHeaders } from '../../api/core.js';

function getAuthHeaders() {
  return { 'Content-Type': 'application/json', ...getCoreAuthHeaders() };
}

// API functions with authentication
const emailTemplateAPI = {
  getAll: async () => {
    const res = await fetch(`${API_BASE_URL}/email_templates`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },
  getById: async (id) => {
    const res = await fetch(`${API_BASE_URL}/email_templates/${id}`, {
      headers: getAuthHeaders()
    });
    return res.json();
  },
  create: async (data) => {
    const res = await fetch(`${API_BASE_URL}/email_templates`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return res.json();
  },
  update: async (id, data) => {
    const res = await fetch(`${API_BASE_URL}/email_templates/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return res.json();
  },
  delete: async (id) => {
    const res = await fetch(`${API_BASE_URL}/email_templates/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return res.json();
  },
  preview: async (id, entityType, entityId) => {
    const res = await fetch(`${API_BASE_URL}/email_templates/${id}/preview`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ entity_type: entityType, entity_id: entityId })
    });
    return res.json();
  },
  getVariables: async (categories) => {
    const res = await fetch(`${API_BASE_URL}/email_templates/variables/${categories}`, {
      headers: getAuthHeaders()
    });
    return res.json();
  }
};

// Category configuration — reuses the same labelKeys as EmailTemplateEditor's CATEGORIES
// (same six categories, same keys), resolved at render time via t().
const CATEGORIES = {
  insurance: { labelKey: 'emails.editor.categories.insurance', color: 'blue', icon: '🛡️' },
  maintenance: { labelKey: 'emails.editor.categories.maintenance', color: 'orange', icon: '🔧' },
  driver: { labelKey: 'emails.editor.categories.driver', color: 'green', icon: '👤' },
  vehicle: { labelKey: 'emails.editor.categories.vehicle', color: 'purple', icon: '🚗' },
  inspection: { labelKey: 'emails.editor.categories.inspection', color: 'yellow', icon: '📋' },
  admin: { labelKey: 'emails.editor.categories.admin', color: 'gray', icon: '⚙️' }
};

function EmailTemplatesView({ onMessage }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('templates'); // 'templates' | 'history' | 'triggers'
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Fetch templates
  const { data: templatesData, isLoading, error } = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: emailTemplateAPI.getAll
  });

  const templates = templatesData?.data || [];

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: emailTemplateAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['emailTemplates']);
      onMessage?.({
        title: t('common.messages.successTitle'),
        message: t('emails.templatesView.toasts.deleted'),
        type: 'success'
      });
    },
    onError: (err) => {
      onMessage?.({
        title: t('common.messages.errorTitle'),
        message: `${t('common.errorContexts.delete')}: ${err.message}`,
        type: 'error'
      });
    }
  });

  // Filter templates
  const filteredTemplates = templates.filter(tpl => {
    const matchesSearch = searchTerm === '' ||
      tpl.template_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tpl.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tpl.template_code?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || tpl.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Group by category
  const groupedTemplates = filteredTemplates.reduce((acc, tpl) => {
    const cat = tpl.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(tpl);
    return acc;
  }, {});

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setShowEditor(true);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowEditor(true);
  };

  const handlePreview = (template) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  const handleDelete = (template) => {
    if (window.confirm(t('emails.templatesView.confirmDelete', { name: template.template_name }))) {
      deleteMutation.mutate(template.id);
    }
  };

  const handleDuplicate = (template) => {
    setEditingTemplate({
      ...template,
      id: null,
      template_code: `${template.template_code}_COPY`,
      template_name: `${template.template_name} (Copie)`
    });
    setShowEditor(true);
  };

  const getCategoryStyle = (category) => {
    const config = CATEGORIES[category] || { color: 'gray' };
    const colors = {
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      orange: 'bg-orange-100 text-orange-800 border-orange-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[config.color] || colors.gray;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <span>{t('emails.templatesView.loadError', { message: error.message })}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Mail className="w-6 h-6 text-amber-600" />
              {t('emails.templatesView.title')}
            </h2>
            <p className="text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{templates.length}</span> {t('emails.templatesView.stats.templates', { count: templates.length })}
              <span className="text-gray-300">|</span>
              <span className="text-green-600">{t('emails.templatesView.stats.active', { count: templates.filter(tpl => tpl.is_active).length })}</span>
              <span className="text-gray-300">|</span>
              <span className="text-gray-500">{t('emails.templatesView.stats.categories', { count: Object.keys(CATEGORIES).length })}</span>
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
          >
            <Plus className="w-5 h-5" />
            {t('emails.templatesView.new')}
          </button>
        </div>

        {/* Tabs */}
        <div className="pt-4 border-t border-gray-200">
          <nav className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveTab('templates')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'templates'
                  ? 'bg-amber-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <FileText className="w-4 h-4" />
              {t('emails.templatesView.tabs.templates', { count: templates.length })}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'history'
                  ? 'bg-amber-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <History className="w-4 h-4" />
              {t('emails.templatesView.tabs.history')}
            </button>
            <button
              onClick={() => setActiveTab('triggers')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'triggers'
                  ? 'bg-amber-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Zap className="w-4 h-4" />
              {t('emails.templatesView.tabs.triggers')}
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'templates' ? (
        <>
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative flex-1 min-w-[250px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('emails.templatesView.search.placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option value="all">{t('emails.templatesView.filters.allCategories')}</option>
                  {Object.entries(CATEGORIES).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.icon} {t(config.labelKey)}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => queryClient.invalidateQueries(['emailTemplates'])}
                className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors"
                title={t('common.actions.refresh')}
              >
                <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
              {(searchTerm || categoryFilter !== 'all') && (
                <span className="text-sm text-gray-600">
                  {t('common.pagination.results', { count: filteredTemplates.length })}
                </span>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {Object.entries(CATEGORIES).map(([key, config]) => {
              const count = templates.filter(tpl => tpl.category === key).length;
              return (
                <div
                  key={key}
                  onClick={() => setCategoryFilter(categoryFilter === key ? 'all' : key)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    categoryFilter === key
                      ? getCategoryStyle(key) + ' ring-2 ring-offset-2'
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-2xl mb-1">{config.icon}</div>
                  <div className="font-semibold">{count}</div>
                  <div className="text-sm text-gray-600">{t(config.labelKey)}</div>
                </div>
              );
            })}
          </div>

          {/* Templates Grid */}
          {Object.keys(groupedTemplates).length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600">{t('emails.templatesView.empty.title')}</h3>
              <p className="text-gray-500 mt-1">
                {searchTerm || categoryFilter !== 'all'
                  ? t('emails.templatesView.empty.filtered')
                  : t('emails.templatesView.empty.default')}
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedTemplates).map(([category, catTemplates]) => (
                <div key={category}>
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                    <span className="text-xl">{CATEGORIES[category]?.icon || '📧'}</span>
                    {CATEGORIES[category] ? t(CATEGORIES[category].labelKey) : category}
                    <span className="text-sm font-normal text-gray-500">({catTemplates.length})</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {catTemplates.map(template => (
                      <div
                        key={template.id}
                        className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded border ${getCategoryStyle(template.category)}`}>
                                {template.template_code}
                              </span>
                            </div>
                            {template.is_active ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                          <h4 className="font-semibold text-gray-900 mb-1">{template.template_name}</h4>
                          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{template.subject}</p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <Globe className="w-3 h-3" />
                              <span>{template.language?.toUpperCase() || 'FR'}</span>
                            </div>
                          </div>
                          <LanguageVariantsPanel
                            template={template}
                            onMessage={onMessage}
                            onSelectVariant={(variant) => {
                              if (variant.id !== template.id) {
                                handlePreview(variant);
                              }
                            }}
                          />
                        </div>
                        <div className="border-t border-gray-100 px-4 py-2 bg-gray-50 rounded-b-lg flex justify-end gap-1">
                          <button
                            onClick={() => handlePreview(template)}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                            title={t('emails.templatesView.actions.preview')}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(template)}
                            className="p-2 text-gray-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                            title={t('common.actions.edit')}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDuplicate(template)}
                            className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                            title={t('emails.templatesView.actions.duplicate')}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(template)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title={t('common.actions.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : activeTab === 'history' ? (
        <GeneratedEmailsHistory onMessage={onMessage} />
      ) : activeTab === 'triggers' ? (
        <WorkflowTriggersTab onMessage={onMessage} />
      ) : null}

      {/* Editor Modal */}
      {showEditor && (
        <EmailTemplateEditor
          template={editingTemplate}
          onClose={() => {
            setShowEditor(false);
            setEditingTemplate(null);
          }}
          onSave={() => {
            queryClient.invalidateQueries(['emailTemplates']);
            setShowEditor(false);
            setEditingTemplate(null);
            onMessage?.({
              title: t('common.messages.successTitle'),
              message: editingTemplate?.id ? t('emails.templatesView.toasts.updated') : t('emails.templatesView.toasts.created'),
              type: 'success'
            });
          }}
          onMessage={onMessage}
        />
      )}

      {/* Preview Modal */}
      {showPreview && previewTemplate && (
        <EmailPreviewModal
          template={previewTemplate}
          onClose={() => {
            setShowPreview(false);
            setPreviewTemplate(null);
          }}
          onMessage={onMessage}
        />
      )}
    </div>
  );
}

export default EmailTemplatesView;
